import { useTrackers } from '@ps/web-shared-analytics';
import type { ClassValue } from 'clsx';
import classNames from 'clsx';
import type { SyntheticEvent } from 'react';
import React, { useCallback, useState } from 'react';

import styles from './ExpandablePanel.scss';
import Title from './components/Title/Title';

export interface Props {
  title?: React.ReactElement<{ isOpen: boolean }>;
  children: React.ReactNode;
  isOpenInitially?: boolean;
  isOddsBoost?: boolean;
  onToggle?: () => void;
  isNested?: boolean;
  className?: ClassValue;
  panelName?: string;
  accessibilityLabel?: string;
}

const ExpandablePanel: React.FC<Props> = ({
  title,
  children,
  isOpenInitially = true,
  isOddsBoost,
  onToggle,
  isNested = false,
  className,
  panelName,
  accessibilityLabel
}) => {
  const [isOpen, setOpen] = useState(isOpenInitially);
  const { panelToggled } = useTrackers();
  const onClick = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault();
      if (onToggle) onToggle();
      setOpen((open) => !open);
      panelToggled({
        panelName,
        isOpen: !isOpen
      });
    },
    [isOpen, onToggle, panelToggled, panelName]
  );

  if (isNested) {
    return (
      <div className={classNames(styles.panel, className)} data-testid="sports-expandable-panel">
        <header
          className={classNames(styles.summary, {
            [styles.panelOddsboost]: isOddsBoost
          })}
        >
          {title && React.cloneElement(title)}
        </header>
        {children}
      </div>
    );
  }

  return (
    <details
      open={isOpen}
      data-testid="sports-expandable-panel"
      className={classNames(styles.panel, className)}
      aria-label={accessibilityLabel}
    >
      <summary
        onClick={onClick}
        className={classNames(styles.summary, {
          [styles.panelOddsboost]: isOpen && isOddsBoost
        })}
        aria-expanded={isOpen}
        role="button"
      >
        {title && React.cloneElement(title, { isOpen })}
      </summary>
      {children}
    </details>
  );
};

export type { Props as ExpandablePanelProps };
export { ExpandablePanel, Title };
